// Electrical circuit-generation engine — derives a base panel/circuit design
// from the Room Takeoff (rooms + elec discipline criteria) and the Process
// Equipment register (electrical connections), so the estimator amends a
// generated starting point instead of building circuits from a blank grid.
// Pure functions only (no DB access) — mirrors the takeoffGeometry.js/
// takeoffMath.js convention. Coordinates are WORLD FEET, same system as the
// Room Takeoff layout canvas (CanvasX/CanvasY on both TakeoffRooms and
// EstimatePanels).
//
// Output circuit rows are shaped for sp_ReplaceEstimateCircuits' @payload.circuits
// (db/24_process_equipment.sql) — one call per panel. Nothing here computes a
// $ cost; RouteLF is an input the pricing layer (estimatePricing.js) later
// combines with an Assembly.

import { rectOf } from "./takeoffGeometry";
import { tryEvaluateFormula } from "./formulaEngine";
import { calcLightingSystem } from "./roomSystemsEngine";
import { resolveLegacyCount } from "../config/roomElements";
import { proposePhases, PAYLOAD_KEYS } from "./panelLoads";

export const DEFAULT_PANEL_NAME = "LP-1";

// Room centroid in world feet, or null if the room isn't placed on the canvas.
export function roomCentroid(room) {
  const r = rectOf(room);
  if (!r) return null;
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

export function distanceFt(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// Centroid of every placed room — used to site the auto-created default panel
// when a revision has none yet.
export function centroidOfRooms(rooms) {
  const centroids = rooms.map(roomCentroid).filter(Boolean);
  if (!centroids.length) return null;
  const x = centroids.reduce((s, c) => s + c.x, 0) / centroids.length;
  const y = centroids.reduce((s, c) => s + c.y, 0) / centroids.length;
  return { x, y };
}

// Nearest panel to a room by canvas distance. Panels or rooms without a
// canvas position can't be geometrically assigned — falls back to the first
// panel (still lets the estimator reassign manually).
export function nearestPanelForRoom(room, panels) {
  if (!panels?.length) return null;
  const centroid = roomCentroid(room);
  if (!centroid) return panels[0];
  let best = null, bestDist = Infinity;
  for (const p of panels) {
    if (p.CanvasX === null || p.CanvasX === undefined) continue;
    const d = distanceFt(centroid, { x: +p.CanvasX, y: +p.CanvasY });
    if (d < bestDist) { bestDist = d; best = p; }
  }
  return best || panels[0];
}

// Homerun length: routing factor over the plan distance, plus a fixed
// vertical allowance (training doc §8 — "Estimated Homerun = Measured Plan
// Distance x Routing Factor + Vertical Allowance"). Null when either end
// lacks a canvas position (nothing to measure geometrically).
//
// When settings.HomerunFormulaID resolves in formulasById (db/29's
// EstimateCostFormulas — editable from the Assembly Library UI, no code deploy
// needed), that formula computes the result instead; an unset or
// unresolvable formula falls back to the hardcoded math below unchanged.
export function computeHomerunLF(room, panel, settings, formulasById = new Map()) {
  if (!panel || panel.CanvasX === null || panel.CanvasX === undefined) return null;
  const centroid = roomCentroid(room);
  if (!centroid) return null;
  const planDistanceFt = distanceFt(centroid, { x: +panel.CanvasX, y: +panel.CanvasY });

  const formula = formulasById.get(settings.HomerunFormulaID);
  if (formula) {
    const viaFormula = tryEvaluateFormula(formula.Expression, {
      planDistanceFt, routingFactor: settings.RoutingFactor, verticalAllowanceFt: settings.VerticalAllowanceFt,
    });
    if (viaFormula !== null) return viaFormula;
  }
  return planDistanceFt * settings.RoutingFactor + settings.VerticalAllowanceFt;
}

// Device-to-device routing within one circuit (training doc §9): spacing x
// (deviceCount - 1), never negative.
export function deviceToDeviceLF(deviceCount, settings) {
  return settings.DeviceSpacingFt * Math.max(0, deviceCount - 1);
}

// Branch circuit route length: homerun + device-to-device + waste, or the
// revision's BranchRouteFormulaID when it resolves (same fallback contract
// as computeHomerunLF).
function computeBranchRouteLF(homerunLF, deviceCount, settings, formulasById) {
  const formula = formulasById.get(settings.BranchRouteFormulaID);
  if (formula) {
    const viaFormula = tryEvaluateFormula(formula.Expression, {
      homerunLF, deviceSpacingFt: settings.DeviceSpacingFt, deviceCount, wasteFactorPct: settings.WasteFactorPct,
    });
    if (viaFormula !== null) return viaFormula;
  }
  return (homerunLF + deviceToDeviceLF(deviceCount, settings)) * (1 + settings.WasteFactorPct);
}

// Dedicated equipment feeder route length: homerun + waste, or the
// revision's FeederRouteFormulaID when it resolves.
function computeFeederRouteLF(homerunLF, settings, formulasById) {
  const formula = formulasById.get(settings.FeederRouteFormulaID);
  if (formula) {
    const viaFormula = tryEvaluateFormula(formula.Expression, { homerunLF, wasteFactorPct: settings.WasteFactorPct });
    if (viaFormula !== null) return viaFormula;
  }
  return homerunLF * (1 + settings.WasteFactorPct);
}

// Room's receptacle count (P0 Phase D migration, 2026-09-12). A room that
// carries a receptacle Room Element — an Item/Assembly tagged
// SyncsToCriteriaField='outlet_count' (Setup ▸ Estimate Engine ▸ Items) — is
// counted from that element's EFFECTIVE quantity (fixed / placed / formula,
// with its override; several such elements in a room sum), resolved by the
// same resolveElementQuantity the flyout and Estimate use. A room without one
// falls back to the legacy per-room outlet_count criterion chain (own row →
// room template default → project default), so existing revisions generate
// exactly the circuits they did before. `systemsCtx.roomElementsByRoom`
// (groupElementsByRoom), `.takeoffConditions` (placed symbols) and
// `.formulas` are the inputs; without the first the legacy path applies.
// The rule itself is roomElements.js resolveLegacyCount — shared with the
// room-formula variables and the Utility demand view so every reader of a
// migrated legacy count agrees.
export function receptacleCount(room, criteria, systemsCtx = {}) {
  const r = resolveLegacyCount("outlet_count", room, criteria, systemsCtx);
  return { outlets: r.value, source: r.source, elements: r.elements };
}

// Room's elec device counts. Outlets come from the room's receptacle element
// when it has one, else the legacy outlet_count criterion (receptacleCount
// above; `outletsSource` says which). Lighting fixtures are the engineering layer's
// EFFECTIVE count (roomSystemsEngine.js: the room's own override, else the
// lumen-method calculation, else the legacy template/project default) — this
// engine is downstream of that calculation, it doesn't own the fixture count.
// `systemsCtx` = { bodAttrs, settings, tunables }; without it the engine
// resolves at its built-in defaults, which for a fixture option with no
// lumen data is exactly the pre-2026-09-11 behavior.
//
// vaPerFixture is the selected fixture's real VA when Basis of Design has it
// (only present in the result then), else the caller falls back to
// settings.VaPerLightingFixture.
export function roomDeviceCounts(room, criteria, systemsCtx = {}) {
  const receptacles = receptacleCount(room, criteria, systemsCtx);
  const lighting = calcLightingSystem(room, criteria, systemsCtx);
  const result = { outlets: receptacles.outlets, fixtures: lighting.effectiveQuantity, outletsSource: receptacles.source };
  if (receptacles.elements.length) result.receptacleElements = receptacles.elements;
  if (lighting.vaSource === "option" || lighting.vaSource === "option_watts") result.vaPerFixture = lighting.vaPerFixture;
  return result;
}

// Split a device count into circuit-sized groups (training doc §3/§6 —
// "group devices into realistic branch circuits" up to a company-standard
// cap, not a literal construction-level circuit design).
function splitIntoGroups(count, perCircuit) {
  const groups = [];
  let remaining = count;
  while (remaining > 0) {
    const n = Math.min(perCircuit, remaining);
    groups.push(n);
    remaining -= n;
  }
  return groups;
}

// One room's branch circuits (receptacles + lighting), route length included.
// homerunLF may be null (room/panel not placed on canvas yet) — RouteLF is
// then left null for the estimator to enter manually.
//
// Lighting connected VA prefers the selected fixture's own VA (Basis of
// Design attribute, via roomDeviceCounts) and falls back to the revision's
// VaPerLightingFixture setting only when no fixture data exists.
export function buildBranchCircuitsForRoom(room, criteria, settings, homerunLF, formulasById = new Map(), systemsCtx = {}) {
  const { outlets, fixtures, vaPerFixture } = roomDeviceCounts(room, criteria, { ...systemsCtx, settings: systemsCtx.settings ?? settings });
  const circuits = [];

  const groupsFor = (count, loadClass, vaPerDevice, label) =>
    splitIntoGroups(count, settings.DevicesPerCircuit).map((deviceCount, i, arr) => {
      const routeLF = homerunLF === null ? null
        : round1(computeBranchRouteLF(homerunLF, deviceCount, settings, formulasById));
      return {
        load_class: loadClass,
        circuit_desc: `${label} - ${room.RoomName}${arr.length > 1 ? ` [${i + 1}/${arr.length}]` : ""}`,
        connected_va: deviceCount * vaPerDevice,
        room_id: room.RoomID,
        route_lf: routeLF,
        device_count: deviceCount, // informational only — not a stored column, dropped before save
      };
    });

  circuits.push(...groupsFor(outlets, "receptacle", settings.VaPerReceptacle, "REC"));
  circuits.push(...groupsFor(fixtures, "lighting", vaPerFixture ?? settings.VaPerLightingFixture, "LTG"));
  return circuits;
}

// One dedicated circuit per electrical equipment connection serving this room
// (training doc §11 — "equipment gets dedicated circuits"). connections here
// are EquipmentConnections rows already filtered to UtilityType='electrical'
// and joined to their ProcessEquipment row (equipment.RoomID === room.RoomID).
export function buildEquipmentCircuitsForRoom(room, equipmentConnections, settings, homerunLF, formulasById = new Map()) {
  return equipmentConnections.map((c) => {
    const routeLF = homerunLF === null ? null : round1(computeFeederRouteLF(homerunLF, settings, formulasById));
    const poles = c.Phase === 3 ? 3 : /240/.test(c.Volts || "") ? 2 : 1;
    return {
      load_class: "equipment",
      circuit_desc: `${c.EquipmentTag || c.CircuitDesc || "EQUIP"} - ${room.RoomName}`,
      connected_va: c.KW ? c.KW * 1000 : null,
      poles,
      trip_amps: c.MOCP || null,
      connection_id: c.ConnectionID,
      room_id: room.RoomID,
      route_lf: routeLF,
    };
  });
}

const round1 = (n) => Math.round(n * 10) / 10;

// Full generation pass. Inputs:
//   rooms       - TakeoffRooms rows
//   panels      - EstimatePanels rows already in this revision (may be empty)
//   criteria    - flat criterionKey -> value map (src/config/takeoffCriteria.js)
//   equipment   - ProcessEquipment rows
//   connections - EquipmentConnections rows (all utility types; filtered here)
//   settings    - EstimateCostSettings row
//   systemsCtx  - { bodAttrs, tunables } for roomSystemsEngine.js (lighting
//                 fixture count/VA from the selected fixture's Basis of Design
//                 attributes); optional — defaults resolve without it
//
// Returns { needsDefaultPanel, defaultPanelCentroid, circuitsByPanelId } —
// when needsDefaultPanel is true, the caller creates the panel first (via
// sp_SaveEstimatePanel at defaultPanelCentroid) and re-runs generation with it
// included in `panels`. circuitsByPanelId values are ready for
// sp_ReplaceEstimateCircuits' @payload.circuits (numbered sequentially; the
// estimator can renumber to match panel-face odd/even conventions after).
export function generateCircuitPlan({ rooms, panels, criteria, equipment, connections, settings, formulasById = new Map(), systemsCtx = {} }) {
  if (!panels?.length) {
    return { needsDefaultPanel: true, defaultPanelCentroid: centroidOfRooms(rooms), circuitsByPanelId: {} };
  }

  const equipById = new Map(equipment.map((e) => [e.EquipmentID, e]));
  const elecConnsByRoom = new Map();
  for (const c of connections) {
    if (c.UtilityType !== "electrical") continue;
    const equip = equipById.get(c.EquipmentID);
    if (!equip?.RoomID) continue;
    if (!elecConnsByRoom.has(equip.RoomID)) elecConnsByRoom.set(equip.RoomID, []);
    elecConnsByRoom.get(equip.RoomID).push({ ...c, EquipmentTag: equip.Tag || equip.Description });
  }

  const circuitsByPanelId = {};
  for (const room of rooms) {
    const panel = nearestPanelForRoom(room, panels);
    if (!panel) continue;
    const homerunLF = computeHomerunLF(room, panel, settings, formulasById);

    const roomCircuits = [
      ...buildBranchCircuitsForRoom(room, criteria, settings, homerunLF, formulasById, systemsCtx),
      ...buildEquipmentCircuitsForRoom(room, elecConnsByRoom.get(room.RoomID) || [], settings, homerunLF, formulasById),
    ];
    if (!roomCircuits.length) continue;

    (circuitsByPanelId[panel.PanelID] ||= []).push(...roomCircuits);
  }

  // Number sequentially per panel and drop the informational-only device_count.
  // Then PROPOSE an explicit phase per circuit by least-loaded balancing
  // (panelLoads.js proposePhases — 1 pole → lightest phase, 2 → lightest
  // pair, 3 → ABC) so the stored row carries its own phase; the circuit
  // number is never the source of a phase (P1 Phase D-2 product decision).
  const panelsById = new Map(panels.map((p) => [String(p.PanelID), p]));
  for (const panelId of Object.keys(circuitsByPanelId)) {
    const numbered = circuitsByPanelId[panelId].map((c, i) => {
      const { device_count, ...row } = c;
      return { circuit_number: i + 1, poles: 1, ...row };
    });
    circuitsByPanelId[panelId] = proposePhases(numbered, panelsById.get(panelId) || { Phases: 3 }, PAYLOAD_KEYS);
  }

  return { needsDefaultPanel: false, defaultPanelCentroid: null, circuitsByPanelId };
}
